import type { AuthUserDto } from '../generated/contracts';
import { getSession, logout } from '../net/api';
import { SignIn } from '../ui/SignIn';

/**
 * The account this browser is signed in as, asking for credentials if it is not.
 *
 * A returning visitor with a live cookie never sees a form — one round trip and the caller
 * carries on. This used to live inline in `main.ts`'s own composition-root body; pulled out
 * so `Router`'s auth guard (`Router.ts`, `plan/identity-and-portal.md` migration step 3) and
 * the room route's own boot path share exactly one place a signed-in identity comes from,
 * rather than each asking the server separately in a way that could disagree. The router
 * itself never calls this or the server — it only knows `true`/`false` from whatever `guard`
 * returns; `main.ts` is what actually shows this UI and retries the navigation once it
 * resolves. See `Router.ts`'s own doc comment for why that split is what keeps `Router`
 * testable with a fake guard.
 *
 * `authOrigin` is threaded through to {@link SignIn} and matters for exactly one caller: the VR
 * client, whose document is served by a host that does not own the OAuth callback. See that
 * class's own remarks — this parameter is only a pass-through.
 */
export async function ensureSignedIn(host: HTMLElement, authOrigin = ''): Promise<AuthUserDto> {
  const existing = await getSession();

  if (existing !== null) {
    return existing;
  }

  const form = new SignIn(host, authOrigin);

  try {
    return await form.waitForSignIn();
  } finally {
    form.dispose();
  }
}

/**
 * Ends the session server-side and lands on `/`, signed out.
 *
 * A full navigation (`location.assign`), not an in-app state reset: both surfaces that offer
 * this (the portal shell, the VR entry screen) can have a live `RoomHub` connection, a
 * running Babylon engine, or an open WebXR session underneath them, and safely tearing all of
 * that down in place is a bigger job than a sign-out button justifies — the owner has already
 * said the VR entry screen gets a proper cleanup pass later. A reload is simple, correct
 * (nothing survives it that shouldn't), and sign-out is not a hot path worth optimising away
 * the reload for.
 *
 * **`/` on purpose, not `/portal`.** This used to land on `/portal` directly, which is how a
 * headset that signed out landed back on the desktop portal after signing back in: the browser
 * sat on `/portal`, `SignIn.rememberDestination()` parked that as the destination (a real route,
 * correctly accepted by `takeRememberedDestination`'s allowlist), and the Google callback honoured
 * it — landing a headset visitor on the desktop portal exactly as reported
 * (`plan/session-exit-and-membership-fixes.md`, A4). `/` is not a route; it is the one path
 * `Router.resolveDefault` is ever consulted for, so landing here lets capability — not this
 * function's own guess — decide the next landing for whoever actually signs back in. A deliberate
 * deep link to `/portal` from a headset, remembered the same way, still lands on `/portal`; only
 * sign-out's own unconditional destination changes.
 */
export async function signOut(): Promise<void> {
  await logout();
  window.location.assign('/');
}
